import type { FC } from 'react';
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from '@interstate/components/Alert';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { Interstitial } from '@interstate/components/Interstitial';
import { TOAST_TIMEOUT } from '@interstate/components/Toast/Toast/store';

import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';
import * as dealTemplateActionCreators from '@makemydeal/dr-dash-store/actions/dealTemplateActionCreators';
import type { DraftDealTemplate, ParamsToSaveTemplate } from '@makemydeal/dr-dash-types';
import { TEST_IDS } from '../../constants';
import { useToastDisappear } from '../../../../common/DataLoadingToast';
import TemplateSaveStatusDialog from '../../../saveTemplate/TemplateSaveStatusDialog.interstate';
import { processTemplatePayload } from '../sections/utils';

import { useTemplateForm } from '../hooks/useTemplateForm';
import { useTemplateValidation } from '../hooks/useTemplateValidation';
import { BaseTemplateForm } from './BaseTemplateForm';

export interface SaveTemplateFormProps {
    handleCancel: () => void;
    handleSaveTemplate: () => void;
    isActive: boolean;
}

export const SaveTemplateForm: FC<SaveTemplateFormProps> = ({ handleCancel, handleSaveTemplate, isActive }) => {
    const dispatch = useDispatch();

    const savePaymentTerms = useSelector(dealTemplateSelectors.getSavePaymentTerms);
    const {
        isSaving,
        isSaved,
        isError: isSavingError,
        errorDetails
    } = useSelector(dealTemplateSelectors.getDraftDealTemplate) as DraftDealTemplate;

    const { formState, formActions } = useTemplateForm();
    const { isSubmitDisabled } = useTemplateValidation(formState);

    const [showTemplateSaveStatus, setShowTemplateSaveStatus] = useState<boolean>(false);

    const { toastShown: errorToastShown, triggerToast: showErrorToast, hideToast } = useToastDisappear(TOAST_TIMEOUT);

    const wasSavingRef = useRef<boolean>(isSaving);

    const togglePaymentTerms = (checked: boolean) => {
        dispatch(dealTemplateActionCreators.toggleSavePaymentTerms(checked));
    };

    const handleSave = () => {
        setShowTemplateSaveStatus(false);

        const { templateName, templateDescription, inventoryTypes, years, makes, models, trims, selectedScopeIds } = formState;

        const savePayload = processTemplatePayload({
            name: templateName,
            ...(templateDescription && { description: templateDescription }),
            inventoryTypes,
            years,
            makes,
            models,
            trims
        }) as ParamsToSaveTemplate;

        dispatch(dealTemplateActionCreators.saveTemplate({ ...savePayload, selectedScopeIds }));
    };

    useEffect(() => {
        if (isActive) {
            formActions.initializeFromVehicle();
        }
    }, [formActions.initializeFromVehicle, isActive]);

    useEffect(() => {
        if (isSaving) {
            wasSavingRef.current = true;
        }
        if (!isActive) {
            hideToast();
        }
    }, [isSaving, isActive, hideToast]);

    useEffect(() => {
        if (!wasSavingRef.current) return;

        if (isSaved || isSavingError) {
            togglePaymentTerms(false);
            wasSavingRef.current = false;
        }

        const isTemplateNameTaken = errorDetails?.name === 'TemplateNameConflictException';

        if (isSavingError && isTemplateNameTaken) {
            formActions.setTemplateNameConflictError(
                `The template name "${formState.templateName}" is already in use. Please choose a different name before saving`
            );
        }

        if (isSavingError && !isTemplateNameTaken) {
            showErrorToast();
        }

        if (isSaved) {
            handleSaveTemplate();
            formActions.resetForm();
        }
    }, [isSaved, isSavingError, errorDetails, formState.templateName, formActions, showErrorToast, handleSaveTemplate]);

    return (
        <Box data-testid={TEST_IDS.SAVE_TEMPLATE_WRAPPER}>
            {isSaving && <Interstitial data-testid={TEST_IDS.TEMPLATE_SAVE_V2_LOADER} fullScreen size="small" />}
            {errorToastShown && (
                <Alert type="error" data-testid={TEST_IDS.TEMPLATE_SAVE_ERROR_TOAST} title="Failed to save template">
                    This deal encountered an unforeseen issue, and the deal template could not be saved.
                </Alert>
            )}
            <TemplateSaveStatusDialog
                handleSave={handleSave}
                show={showTemplateSaveStatus}
                onHide={() => {
                    togglePaymentTerms(false);
                    setShowTemplateSaveStatus(false);
                }}
                toggleSavePaymentTerms={togglePaymentTerms}
                savePaymentTerms={savePaymentTerms}
            />
            <BaseTemplateForm formState={formState} formActions={formActions} disabled={false} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    onClick={handleCancel}
                    data-testid={TEST_IDS.CANCEL_SAVE_TEMPLATE_V2_BUTTON}
                    buttonStyle="tertiary"
                    sx={{ mr: 2 }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={() => setShowTemplateSaveStatus(true)}
                    data-testid={TEST_IDS.SAVE_TEMPLATE_V2_BUTTON}
                    buttonStyle="primary"
                    disabled={isSubmitDisabled}
                >
                    Save Template
                </Button>
            </Box>
        </Box>
    );
};
